import { BaseApiClient } from './base.client';
import type { NearbyShow, NearbySportEvent } from './sportsevents.client';
import type { HotelListRecord } from './hotels.client';

export interface RestaurantAddress {
  address1: string | null;
  city: string | null;
  /** Region/state/province (e.g. "Greater London") — resOS's
   *  `address.state`. Null when resOS didn't provide one for this
   *  restaurant. */
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface RestaurantLocation {
  lat: number;
  lng: number;
}

export interface RestaurantRecord {
  _id: string;        // Mongo id
  id: string;         // resOS id

  // resOS partner-API shape — stored verbatim
  name: string;
  description: string | null;
  image: string | null;
  address: RestaurantAddress | null;
  location: RestaurantLocation | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  addOns: Record<string, unknown>;

  // Derived slugs for URL routing
  slug: string;
  countrySlug: string;
  stateSlug: string;
  citySlug: string;

  IsActive: boolean;
  WebSiteID: string;
  CreatedDate: string;
  UpdatedDate: string;
  /** When true, the booking flow skips table selection straight to
   *  checkout if the chosen slot has zero available tables. */
  IsTableSelectionOptional?: boolean;

  // Scoring annotations attached server-side when the listing query
  // carries enough context (date+people for slot scoring, userLat+userLon
  // for distance scoring, `search` for name-relevance scoring). Always
  // present in the response shape; `score` is the sum of the per-parameter
  // scores below. Results already come back sorted by `score` DESC with
  // alphabetical A→Z tiebreaker — callers should render `data` in order.
  score?: number;
  distanceMiles?: number | null;
  distanceScore?: number;
  slotsAvailable?: number;
  slotsScore?: number;
  /** Tiered text-relevance score (100/80/60/40/20/0). Populated when the
   *  caller sends `search` — 0/absent otherwise. */
  nameScore?: number;
}

export interface RestaurantListResponse {
  data: RestaurantRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SearchRestaurantsParams {
  city?: string;
  country?: string;
  state?: string;
  search?: string;
  restaurantId?: string;
  cuisine?: string;
  priceRange?: string;
  rating?: number;
  amenities?: string;
  date?: string;
  time?: string;
  people?: number;
  userLat?: number;
  userLon?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface OpeningHourRecord {
  day: number;       // 1 = Monday … 7 = Sunday
  openTime: string;  // "HH:MM" or "HH:MM:SS"
  closeTime: string;
}

export interface AvailabilitySlot {
  time: string;
  available: boolean;
  maxPartySize?: number;
}

export interface AvailableTable {
  tableId: string;
  name: string;
  minCapacity: number;
  maxCapacity: number;
}

/** Trimmed-to-display row from `/bookingFlow/availableTables` — only
 *  the fields the table-selection card needs. Excludes booked rows
 *  and non-online-bookable rows server-side. */
export interface BookingFlowTable {
  _id: string;
  name: string;
  type: string | null;
  seatsMin: number | null;
  seatsMax: number | null;
  bookingPriority: number | null;
  area: {
    _id: string;
    name: string;
    bookingPriority: number | null;
  } | null;
}

/** resOS `/bookingFlow/times` returns an array of opening-hour blocks,
 *  each carrying its own `availableTimes` / `unavailableTimes` arrays of
 *  `"HH:mm"` strings. Multiple blocks can coexist (e.g. a lunch window
 *  and a dinner window), so consumers should flatten `availableTimes`
 *  across blocks for a complete slot list. */
export interface BookingFlowTimesBlock {
  _id: string;
  name?: string;
  note?: string;
  payment?: { active?: boolean };
  availableTimes?: string[];
  unavailableTimes?: string[];
  waitlistTimes?: string[];
  useWaitlist?: boolean;
  activeCustomFields?: unknown[];
  [key: string]: unknown;
}

export type BookingFlowTimesResponse = BookingFlowTimesBlock[];

export interface AvailableDateRange {
  availableDates: string[];
}

export interface CreateRestaurantBookingParams {
  resosRestaurantId: string;
  restaurantId: string;
  // Restaurant display fields snapshotted at booking time — pass the
  // already-fetched RestaurantRecord's name/image/address/city/state.
  restaurantName?: string;
  restaurantImage?: string;
  restaurantCountry?: string;
  restaurantAddress?: string;
  restaurantCity?: string;
  restaurantPostalCode?: string;
  date: string;
  time: string;
  people: number;
  duration?: number;
  tableId?: string;
  // Human-readable table label ("Table 3", "Window 12"). Optional —
  // resOS auto-assigned bookings (area only, or no specific table)
  // omit it. Persisted alongside `tableId` so the confirmation surface
  // renders the label instead of the opaque resOS id.
  tableName?: string;
  // Area name ("Bar", "Garden") and resOS `type` string ("Standard",
  // "High Top"). Persisted so downstream surfaces can compose the
  // "N People (Area - Type)" guest line without a resOS round-trip.
  tableArea?: string;
  tableType?: string;
  guestFirstName: string;
  guestLastName: string;
  guestEmail: string;
  guestPhone: string;
  notes?: string;
  memberId?: string;
  // Marketing-consent checkbox from the checkout form. Optional so
  // older callers stay compatible; the backend defaults to `false`.
  marketingConsent?: boolean;
  // Full name of the person BOOKING — distinct from the diner's
  // `guestFirstName`/`guestLastName` when the customer used the
  // "booking for someone else" toggle.
  bookerName?: string;
}

export interface LocationSuggestion {
  _id: string;
  city: string;
  state: string | null;
  country: string | null;
  country_code: string | null;
  /** Tiered text-relevance score (100/80/60/40/0) against the query —
   *  same tiers/algorithm `RestaurantRecord.score` uses for restaurant-
   *  name search. Results already come back sorted by `score` DESC
   *  with alphabetical A→Z tiebreaker. */
  score?: number;
}

/** SearchLocation lookup envelope — mirrors the sports endpoint shape. */
export interface CityDestIdResult {
  message: 'success' | 'not_found' | 'error';
  data: Record<string, any> | null;
  error?: string;
}

export interface RestaurantBooking {
  _id: string;
  // Sequential reservation id from `Restaurants_Counter` — `BPR{n}`.
  // Mirrors the sports service's `BSP{n}` pattern.
  reservationId?: string;
  // resOS ids — populated after `POST /bookings` succeeds.
  resosReservationId: string;
  resosRestaurantId?: string;
  // Our restaurant record's Mongo `_id`.
  restaurantId: string;
  // Restaurant display fields snapshotted at booking time.
  restaurantName: string;
  restaurantImage: string;
  restaurantCountry: string;
  restaurantAddress: string;
  // Always present — empty string when the source restaurant record
  // had no city rather than the key being omitted.
  restaurantCity: string;
  restaurantPostalCode: string;
  bookingDate: string;
  bookingTime: string;
  // `bookingDate` + `bookingTime` combined into one ISO-8601 string.
  bookingDateTime: string;
  guestCount: number;
  tableId: string;
  // Human-readable label chosen at booking time.
  tableName: string;
  // Area + type at pick time — persisted to compose the guest line.
  tableArea: string;
  tableType: string;
  duration: number;
  bookingStatus: string;
  paymentStatus: string;
  totalAmount: number;
  resosStatus: string;
  // resOS's create-booking response is a bare id string, not an
  // object — stored verbatim, so this can be a string as well as an
  // object depending on which resOS call last wrote it.
  resosResponse?: unknown;
  guestFirstName: string;
  guestLastName: string; 
  guestEmail: string;
  guestPhone: string;
  notes?: string;
  // Consent + booker fields captured at booking-create time. See the
  // schema doc-comment for GDPR / booker-vs-diner semantics.
  marketingConsent?: boolean;
  bookerName?: string;
  // CMS-canonical fields keep their PascalCase spelling — see the
  // schema doc-comment for the rationale.
  IsActive?: boolean;
  CreatedDate: string;
  UpdatedDate: string;

  // ── Server-derived display fields (manage-bookings detail page —
  // plan Section 1a; populated by `RestaurantsService.mapBookingDetail`
  // on `getBooking`/`listBookings`, absent on the raw `createBooking`
  // response). `restaurantId` above stays the plain id string on this
  // path too — the joined `Restaurant` doc's fields surface here instead
  // of replacing it. ──
  Name?: string;
  Image?: string;
  VenueDetail?: string;
  BookingDateTime?: string;
  GuestDisplay?: string;
  TableName?: string;
  CustomerName?: string;
  cancellable?: boolean;
  showCancelButton?: boolean;
}

/** One row in the manage-bookings "Restaurants" tab list. Same
 *  `extends <full booking> { bookingType }` shape as
 *  `SportsBookingListItem`/`ShowsBookingListItem`; the card reads the
 *  PascalCase display fields (`Name`/`Image`/`VenueDetail`/…) that
 *  `RestaurantsService.mapBookingDetail` projects onto the record.
 *  `bookingType: 'restaurant'` is the discriminant the unified page's
 *  `AnyBookingItem` union switches on. */
export interface RestaurantBookingListItem extends RestaurantBooking {
  bookingType: 'restaurant';
}

export interface RestaurantBookingsListResponse {
  records: RestaurantBookingListItem[];
  total: number;
  limit: number;
  page: number;
  totalPages: number;
}

export interface NearbySportsParams {
  latitude: number;
  longitude: number;
  distance?: number;
  limit?: number;
  sortBy?: 'distance';
  sortOrder?: 'asc' | 'desc';
  dateFrom?: string;
  userCurrency?: string;
}

export interface NearbySportsResponse {
  success: boolean;
  data: NearbySportEvent[];
  meta: { totalRecords: number };
}

export interface NearbyShowsParams {
  latitude: number;
  longitude: number;
  distance?: number;
  limit?: number;
  dateFrom?: string;
  userCurrency?: string;
}

export interface NearbyShowsResponse {
  shows: NearbyShow[];
  total: number;
}

/**
 * Query params for the "hotels near here" widget — the shape accepted
 * by `/bip-api/common/nearby/hotels` (common-service hub), which
 * forwards to `hotels-service /search/nearby-hotels`. hotels-service
 * uses `forbidNonWhitelisted` validation, so ONLY the fields below are
 * allowed — sending anything else 400s the whole request. `rooms` /
 * `city` / `countryCode` were on this type historically for the old
 * CMS-integration path; they were removed when we moved to the live
 * hotels-service endpoint.
 */
export interface NearbyHotelsParams {
  latitude: number;
  longitude: number;
  checkin?: string;
  checkout?: string;
  adults?: string | number;
  children?: string | number;
  count?: string | number;
  userCurrency?: string;
  nationality?: string;
  refLabel?: string;
  showFilter?: boolean;
}

/**
 * Response shape — matches hotels-service's `SearchListResponse` subset
 * that `/search/nearby-hotels` returns: closest-first `records[]`,
 * `totalCount`, `searchResultsPageUrl?` (only for geo/nearby),
 * `fallbackMeta` (radius-widening banner cue), plus the full facet /
 * meta bag downstream may inspect. Widget only needs `records[]` +
 * `fallbackMeta` for the "N miles from city centre" copy.
 */
export interface NearbyHotelsResponse {
  records: HotelListRecord[];
  totalCount: number;
  searchResultsPageUrl?: string;
  fallbackMeta: {
    isFallbackApplied: boolean;
    radiusMiles?: number;
    bannerMessage?: string;
    cityName?: string;
  };
  // Remaining fields present on the wire but not required by the widget
  // — kept `unknown` here so callers that want them can cast, and the
  // narrow contract stays honest.
  currencyCode?: string;
  currencySymbol?: string;
  totalPages?: number;
  page?: number;
  pageSize?: number;
  meta?: unknown;
  filters?: unknown;
  affiliateVenueMeta?: unknown;
  refVenueMeta?: unknown;
}

export interface NearbyRestaurantsParams {
  latitude: number;
  longitude: number;
  distance?: number;
  limit?: number;
  page?: number;
  sortBy?: 'distance';
  sortOrder?: 'asc' | 'desc';
  city?: string;
}

export interface NearbyRestaurantRecord {
  _id: string;
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  address: RestaurantAddress | null;
  location: RestaurantLocation | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  slug: string;
  countrySlug: string;
  stateSlug: string;
  citySlug: string;
  distance: { value: number; unit: 'miles' };
  distanceInMiles: number;
  detailPageUrl: string | null;
}

export interface NearbyRestaurantsResponse {
  success: boolean;
  message: string;
  meta: {
    latitude: number;
    longitude: number;
    radiusInMiles: number;
    totalRecords: number;
    sortedBy: string;
    page: number;
    limit: number;
  };
  data: NearbyRestaurantRecord[];
  exploreUrl?: string;
}

export class RestaurantsClient extends BaseApiClient {
  constructor(baseUrl?: string) {
    super(baseUrl || process.env.NEXT_PUBLIC_RESTAURANTS_API_URL);
  }

  /** Send the `member_d` session cookie on every call so the member-scoped bookings endpoints can
   *  resolve the memberId server-side (`MemberSessionService`), same convention as `HotelsClient`
   *  / `ShowsClient` / `SportsEventsClient`. */
  protected override async request<T>(
    endpoint: string,
    options?: RequestInit & { next?: { revalidate?: number | false; tags?: string[] }; timeoutMs?: number },
  ): Promise<T> {
    return super.request<T>(endpoint, { ...(options || {}), credentials: 'include' });
  }

  async searchRestaurants(params?: SearchRestaurantsParams): Promise<RestaurantListResponse> {
    return this.get<RestaurantListResponse>('/restaurants', params);
  }

  async getBySlug(
    country: string,
    state: string,
    city: string,
    slug: string,
  ): Promise<RestaurantRecord> {
    return this.get<RestaurantRecord>(`/restaurants/by-slug/${country}/${state}/${city}/${slug}`);
  }

  async getBySlugOnly(slug: string): Promise<RestaurantRecord> {
    const r = await this.get<{
      message: string; data: RestaurantRecord }>(
      `/restaurants/by-slug-only/${slug}`,
    );
    return r.data;
  }

  /**
   * Fetch a single restaurant by its resOS id. Reuses the
   * `searchRestaurants` endpoint (`?restaurantId=` narrow) so no new
   * backend surface is required — the search route already returns
   * exactly one row when this filter is set. Returns `null` when the
   * id doesn't match anything or the request fails; callers should
   * treat that as "record unavailable" rather than crashing.
   */
  async getById(id: string): Promise<RestaurantRecord | null> {
    if (!id) return null;
    try {
      const res = await this.searchRestaurants({ restaurantId: id, limit: 1 });
      return res.data?.[0] ?? null;
    } catch {
      return null;
    }
  }

  async getOpeningHours(resosId: string): Promise<OpeningHourRecord[]> {
    const r = await this.get<{ message: string; data: OpeningHourRecord[] }>(
      `/restaurants/${resosId}/opening-hours`,
    );
    return r.data ?? [];
  }

  async getLocationSuggestions(q: string): Promise<LocationSuggestion[]> {
    if (!q || q.trim().length < 2) return [];
    const r = await this.get<{ message: string; data: LocationSuggestion[] }>(
      '/locations/suggestions',
      { q: q.trim() },
    );
    return r.data;
  }

  /**
   * Resolve a city-type SearchLocation row so the frontend can seed
   * cross-domain recent-search localStorage entries (sports
   * `theatre-searches-city` + hotels `searches[]`) without crossing
   * into the sports client.
   *
   * Returns the same envelope shape as the sports endpoint:
   *   { message: 'success'|'not_found'|'error', data, error? }
   */
  async getCityDestId(cityName: string): Promise<CityDestIdResult> {
    if (!cityName) {
      return { message: 'not_found', data: null, error: 'cityName is required' };
    }
    return this.get<CityDestIdResult>('/locations/city-dest-id', { cityName });
  }

  async getAvailability(
    resosId: string,
    date: string,
    people: number,
    areaId?: string,
  ): Promise<AvailabilitySlot[]> {
    const params: Record<string, string | number> = { date, people };
    if (areaId) params.areaId = areaId;
    const r = await this.get<{ message: string; data: AvailabilitySlot[] }>(
      `/restaurants/${resosId}/availability`,
      params,
    );
    return r.data ?? [];
  }

  /**
   * Real-time booking-flow time slots for the listing card. Returns the
   * raw resOS payload — typically an array of `{ time, available, ... }`
   * objects under `data.times` or directly at the top level; callers
   * should normalize defensively.
   */
  async getBookingFlowTimes(
    resosId: string,
    date: string,
    people: number,
  ): Promise<BookingFlowTimesResponse> {
    const r = await this.get<{ message: string; data: BookingFlowTimesResponse }>(
      `/restaurants/${resosId}/booking-flow/times`,
      { date, people },
    );
    return r.data;
  }

  async getBookingFlowDurations(
    resosId: string,
    date: string,
    time: string,
    people: number,
  ): Promise<number[]> {
    const r = await this.get<{ message: string; data: unknown }>(
      `/restaurants/${resosId}/booking-flow/durations`,
      { date, time, people },
    );
    const raw = r.data;
    if (Array.isArray(raw)) return raw.map(Number).filter(Boolean);
    return [];
  }

  async getAvailableDates(
    resosId: string,
    fromDate: string,
    toDate: string,
  ): Promise<AvailableDateRange> {
    return this.get<AvailableDateRange>(`/restaurants/${resosId}/dates`, { fromDate, toDate });
  }

  async getAvailableTables(
    resosId: string,
    people: number,
    fromDateTime: string,
    toDateTime: string,
  ): Promise<AvailableTable[]> {
    return this.get<AvailableTable[]>(`/restaurants/${resosId}/tables`, {
      people,
      fromDateTime,
      toDateTime,
    });
  }

  /**
   * Table-selection page data — fetches resOS booking-flow tables and
   * pre-filters server-side to only bookable / online / unbooked rows.
   */
  async getBookingFlowAvailableTables(
    resosId: string,
    people: number,
    fromDateTime: string,
    toDateTime: string,
  ): Promise<BookingFlowTable[]> {
    const r = await this.get<{ message: string; data: BookingFlowTable[] }>(
      `/restaurants/${resosId}/booking-flow/available-tables`,
      { people, fromDateTime, toDateTime },
    );
    return r.data ?? [];
  }

  // Backend booking endpoints ALL return `{ message, data: RestaurantBooking }`
  // (see `restaurants.controller.ts` — `createBooking` / `cancelBooking`
  // wrap the service return with `{ message: 'success', data }`).
  // Unwrap `.data` here so callers get the booking directly and don't
  // hit `booking._id === undefined` (the checkout `?ref=` value that
  // then rendered as literal "undefined" on the confirmation page).
  async createBooking(data: CreateRestaurantBookingParams): Promise<RestaurantBooking> {
    const r = await this.post<{ message: string; data: RestaurantBooking }>('/restaurants/bookings', data);
    return r.data;
  }

  /** Member-scoped bookings list (manage-bookings "Restaurants" tab). memberId is resolved
   *  server-side from the session cookie — never sent by the client. */
  async getBookings(params: {
    status?: string;
    page?: number;
    pagesize?: number;
  } = {}): Promise<RestaurantBookingsListResponse> {
    const r = await this.get<{ message: string } & Omit<RestaurantBookingsListResponse, 'records'> & { records: RestaurantBooking[] }>(
      '/restaurants/bookings',
      {
        ...(params.status ? { status: params.status } : {}),
        ...(params.page ? { page: String(params.page) } : {}),
        ...(params.pagesize ? { pagesize: String(params.pagesize) } : {}),
      },
    );
    return {
      records: r.records.map((b) => ({ ...b, bookingType: 'restaurant' as const })),
      total: r.total,
      limit: r.limit,
      page: r.page,
      totalPages: r.totalPages,
    };
  }

  /** Member-scoped booking detail (manage-bookings). memberId is resolved server-side. */
  async getBooking(id: string): Promise<RestaurantBooking> {
    const r = await this.get<{ message: string; data: RestaurantBooking }>(`/restaurants/bookings/${id}`);
    return r.data;
  }

  /** Member-scoped cancel. memberId is resolved server-side. */
  async cancelBooking(id: string): Promise<RestaurantBooking> {
    const r = await this.delete<{ message: string; data: RestaurantBooking }>(`/restaurants/bookings/${id}`);
    return r.data;
  }

  /**
   * VIP newsletter subscribe from the confirmation page. Wire contract
   * mirrors `sportsEventsClient.subscribe`: `POST /bookings/subscribe`
   * with `{ email, bookingId? }` → `{ message }`. Both services upsert
   * into the SAME physical Mongo collection (via the shared
   * `SPORTS_SUBSCRIBERS_COLLECTION` env), so a customer subscribing
   * from a restaurant confirmation lands in the same audience list as
   * one subscribing from a sports confirmation.
   */
  async subscribe(email: string, bookingId?: string): Promise<{ message: string }> {
    return this.post<{ message: string }>('/restaurants/bookings/subscribe', { email, bookingId });
  }

  // ── Cross-sell (nearby sports / shows / hotels) ──────────────────────
  // `getNearbySports` / `getNearbyShows` / `getNearbyHotels` USED to
  // live here as proxies through restaurants-service. They moved to
  // `commonClient.getNearby{Sports,Shows,Hotels}` when the cross-sell
  // hub was consolidated in common-service. Restaurants-service now
  // only exposes its OWN-domain `/nearby-restaurants` endpoint
  // (`getNearbyRestaurants` below) — the common-service hub calls it
  // internally when a page asks for nearby restaurants.

  /**
   * Restaurants-domain-native "nearby restaurants" — an OWN-domain geo
   * search (NOT a cross-sell proxy), so it's mounted at the shallower
   * `/nearby-restaurants` path (not `/restaurants/nearby-restaurants`).
   * Still callable direct for the restaurants domain's own use; the
   * `<Nearby>` cross-sell component goes through `commonClient` for
   * every category, including this one, so a single frontend base URL
   * covers all five strips.
   */
  async getNearbyRestaurants(params: NearbyRestaurantsParams): Promise<NearbyRestaurantsResponse> {
    return this.get<NearbyRestaurantsResponse>('/nearby-restaurants', params as unknown as Record<string, any>);
  }
}

export const restaurantsClient = new RestaurantsClient();
